import { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { CreditCard, RefreshCw, ScanLine, Camera, User, Star, Check, ArrowDownLeft, Unlink, Wifi, WifiOff, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ResultOverlay } from '../../components/ResultOverlay';

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

export default function AdminSumup() {
  const [activeClient, setActiveClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualQr, setManualQr] = useState('');
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [offlineQueue, setOfflineQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rsx_offline_queue') || '[]'); } catch { return []; }
  });
  const scannerRef = useRef(null);
  const syncTimerRef = useRef(null);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => { setIsOnline(true); toast.success('Connexion rétablie !'); };
    const goOffline = () => { setIsOnline(false); toast.error('Mode hors-ligne activé'); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  const load = useCallback(() => {
    if (!navigator.onLine) { setLoading(false); return; }
    Promise.all([api.get('/admin/sumup/active-client'), api.get('/admin/sumup/transactions')])
      .then(([a, t]) => { setActiveClient(a.data?.user_id ? a.data : null); setTransactions(t.data); })
      .catch((err) => console.error('Load error:', err)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto sync every 5 minutes
  const autoSyncRef = useRef(null);
  autoSyncRef.current = autoSync;
  useEffect(() => {
    syncTimerRef.current = setInterval(() => {
      if (navigator.onLine && autoSyncRef.current) {
        autoSyncRef.current();
      }
    }, SYNC_INTERVAL);
    return () => clearInterval(syncTimerRef.current);
  }, []);

  const handleQrScan = async (qrUuid) => {
    if (!navigator.onLine) {
      // Offline: store scan locally
      const offlineScan = { qr_uuid: qrUuid, scanned_at: new Date().toISOString(), synced: false };
      const queue = [...offlineQueue, offlineScan];
      setOfflineQueue(queue);
      localStorage.setItem('rsx_offline_queue', JSON.stringify(queue));
      setActiveClient({ user_id: 'offline', user_name: `QR: ${qrUuid.slice(0, 8)}...`, user_email: 'Mode hors-ligne' });
      setOverlay({ show: true, type: 'success', title: 'QR scanné (hors-ligne)', subtitle: 'Les points seront crédités au retour de la connexion' });
      setScanning(false);
      return;
    }
    try {
      const { data } = await api.get(`/admin/qr-lookup/${qrUuid}`);
      await api.post('/admin/sumup/link-client', { user_id: data.user.user_id });
      setActiveClient({ user_id: data.user.user_id, user_name: data.user.name, user_email: data.user.email, points: data.user.points_solde });
      setOverlay({ show: true, type: 'success', title: `${data.user.name}`, subtitle: `${data.user.points_solde || 0} points - Client lié !` });
      setScanning(false);
      setManualQr('');
    } catch {
      setOverlay({ show: true, type: 'error', title: 'QR code inconnu', subtitle: 'Ce QR code ne correspond à aucun client' });
      setScanning(false);
    }
  };

  const handleSync = async () => {
    if (!navigator.onLine) { toast.error('Pas de connexion Internet'); return; }
    setSyncing(true);
    try {
      const { data } = await api.post('/admin/sumup/sync');
      setLastSync(new Date());
      if (data.new_transactions > 0) {
        const name = data.client_name || activeClient?.user_name || 'Client';
        const pts = data.total_points || 0;
        setOverlay({ show: true, type: 'points',
          title: `${name}`,
          subtitle: `Paiement reçu ! ${pts} points au total` });
        // Client auto-unlinked by backend
        setTimeout(() => { setActiveClient(null); load(); }, 2500);
      } else {
        toast.info('Aucune nouvelle transaction');
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur sync SumUp'); }
    finally { setSyncing(false); }
  };

  const autoSync = async () => {
    if (!navigator.onLine || syncing) return;
    try {
      const { data } = await api.post('/admin/sumup/sync');
      setLastSync(new Date());
      if (data.new_transactions > 0 && activeClient?.user_id) {
        const clientName = activeClient?.user_name || 'Client';
        setOverlay({ show: true, type: 'points',
          title: `+${data.new_transactions} paiement(s) !`,
          subtitle: `${clientName} - Points crédités !` });
        setTimeout(async () => {
          try {
            await api.post('/admin/sumup/link-client', { user_id: null });
            setActiveClient(null);
          } catch (err) { console.error('Unlink error:', err); }
        }, 2500);
        load();
      }
    } catch (err) { console.error('Auto sync error:', err); }
  };

  const syncOfflineQueue = async () => {
    const queue = JSON.parse(localStorage.getItem('rsx_offline_queue') || '[]');
    if (queue.length === 0) return;
    let synced = 0;
    for (const item of queue) {
      if (item.synced) continue;
      try {
        const { data } = await api.get(`/admin/qr-lookup/${item.qr_uuid}`);
        await api.post(`/admin/clients/${data.user.user_id}/points`, {
          amount: 50, description: 'Points événement (sync hors-ligne)', note: `Scan offline: ${item.scanned_at}`
        });
        item.synced = true;
        synced++;
      } catch (err) { console.error('Offline sync item error:', err); }
    }
    if (synced > 0) {
      toast.success(`${synced} scan(s) hors-ligne synchronisé(s) !`);
      setOverlay({ show: true, type: 'points', title: `${synced} scan(s) synchronisé(s)`, subtitle: 'Points crédités depuis le mode hors-ligne' });
    }
    localStorage.setItem('rsx_offline_queue', JSON.stringify(queue.filter(q => !q.synced)));
    setOfflineQueue(queue.filter(q => !q.synced));
  };

  const unlinkClient = async () => {
    if (navigator.onLine) {
      await api.post('/admin/sumup/link-client', { user_id: null }).catch(() => {});
    }
    setActiveClient(null);
  };

  const startCamera = async () => {
    setScanning(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("sumup-qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (text) => { scanner.stop().catch(() => {}); handleQrScan(text); },
        () => {}
      );
    } catch (err) { console.error(err); setScanning(false); }
  };

  const stopCamera = () => {
    if (scannerRef.current) scannerRef.current.stop().catch(() => {});
    setScanning(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-sumup-page">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>{"SumUp & Points"}</h1>
          <div className="flex items-center gap-3 mt-1">
            <Badge className={isOnline ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-[#EF4444]/15 text-[#EF4444]'}>
              {isOnline ? <><Wifi className="w-3 h-3 mr-1" /> En ligne</> : <><WifiOff className="w-3 h-3 mr-1" /> Hors-ligne</>}
            </Badge>
            {lastSync && <span className="text-[10px] text-[#A1A1AA] flex items-center gap-1"><Clock className="w-3 h-3" />Sync: {lastSync.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
            {offlineQueue.length > 0 && <Badge className="bg-yellow-500/15 text-yellow-400">{offlineQueue.length} en attente</Badge>}
          </div>
        </div>
        <Button onClick={handleSync} disabled={syncing || !isOnline} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5 apple-press" data-testid="sync-sumup-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sync...' : 'Synchroniser'}
        </Button>
      </div>

      {/* Auto-sync indicator */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-3 mb-4 flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-[#10B981] pulse-gold' : 'bg-[#EF4444]'}`} />
        <span className="text-xs text-[#A1A1AA]">
          {isOnline ? 'Synchronisation automatique toutes les 5 minutes' : "Mode hors-ligne : les scans QR sont sauvegardés localement et seront synchronisés au retour d'Internet"}
        </span>
      </div>

      {/* Active Client */}
      <div className={`rounded-2xl p-6 mb-6 border ${activeClient ? 'bg-[#10B981]/5 border-[#10B981]/20' : 'bg-[#121212] border-white/5'}`} data-testid="active-client-section">
        {activeClient ? (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="bg-[#10B981]/15 p-3 rounded-xl"><User className="w-6 h-6 text-[#10B981]" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm font-semibold text-[#10B981]">{"Client lié"}</span>
              </div>
              <div className="text-lg font-semibold mt-1 truncate">{activeClient.user_name}</div>
              <div className="text-xs text-[#A1A1AA]">{activeClient.user_email}</div>
            </div>
            {activeClient.points !== undefined && (
              <div className="text-center">
                <div className="text-xl font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{activeClient.points}</div>
                <div className="text-[9px] text-[#A1A1AA]">points</div>
              </div>
            )}
            <Button onClick={unlinkClient} variant="outline" className="border-white/10 rounded-full text-xs shrink-0" data-testid="unlink-client-btn">
              <Unlink className="w-3.5 h-3.5 mr-1" /> {"Délier"}
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-center mb-4">
              <ScanLine className="w-10 h-10 text-[#D4AF37] mx-auto mb-2" />
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>{"Scanner le QR code client"}</h3>
              <p className="text-xs text-[#A1A1AA]">{"Scannez le QR code du client avant d'encaisser avec SumUp"}</p>
            </div>
            {scanning ? (
              <div className="max-w-sm mx-auto">
                <div id="sumup-qr-reader" className="rounded-xl overflow-hidden mb-3" style={{ border: '2px solid #D4AF37' }}></div>
                <div className="text-center"><Button onClick={stopCamera} variant="outline" className="border-white/10 rounded-full text-xs">{"Arrêter"}</Button></div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button onClick={startCamera} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6 apple-press" data-testid="sumup-start-camera">
                  <Camera className="w-4 h-4 mr-2" /> {"Activer la caméra"}
                </Button>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-[#A1A1AA]">ou :</span>
                  <Input value={manualQr} onChange={e => setManualQr(e.target.value)} placeholder="UUID..." data-testid="sumup-manual-qr"
                    className="w-48 bg-[#0A0A0A] border-white/10 rounded-xl text-xs h-9"
                    onKeyDown={e => { if (e.key === 'Enter' && manualQr.trim()) handleQrScan(manualQr.trim()); }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 mb-6">
        <h3 className="font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>{"Comment ça marche"}</h3>
        <div className="grid sm:grid-cols-4 gap-4">
          {[
            { step: "1", title: "Scanner", desc: "Scannez le QR du client" },
            { step: "2", title: "Encaisser", desc: "Encaissez avec SumUp" },
            { step: "3", title: "Auto-sync", desc: "Points crédités en 5 min" },
            { step: "4", title: "Fini", desc: "Client auto-délié" },
          ].map(s => (
            <div key={s.step} className="text-center">
              <div className="text-2xl font-bold text-[#D4AF37]/30 mb-1" style={{ fontFamily: 'Outfit' }}>{s.step}</div>
              <h4 className="text-sm font-semibold mb-0.5">{s.title}</h4>
              <p className="text-[10px] text-[#A1A1AA]">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent SumUp transactions */}
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{"Transactions récentes"}</h3>
        {transactions.length === 0 ? (
          <p className="text-sm text-[#A1A1AA] text-center py-8">{"Aucune transaction importée"}</p>
        ) : (
          <div className="space-y-2">
            {transactions.slice(0, 20).map(tx => (
              <div key={tx.sumup_tx_id} className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`sumup-tx-${tx.sumup_tx_id}`}>
                <div className="bg-[#10B981]/10 p-1.5 rounded-md"><ArrowDownLeft className="w-4 h-4 text-[#10B981]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{tx.amount?.toFixed(2)} {tx.currency}</div>
                  <div className="text-[10px] text-[#A1A1AA]">{tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                </div>
                {tx.user_id ? (
                  <Badge className="bg-[#10B981]/15 text-[#10B981] text-[10px]">
                    <Star className="w-2.5 h-2.5 mr-1" /> +{tx.points_credited} pts
                  </Badge>
                ) : (
                  <Badge className="bg-white/5 text-[#A1A1AA] text-[10px]">Pas de client</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
